import { Timestamp } from '@google-cloud/firestore';
import {
  ITranslationMap,
  type IGetRecordResponse,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ChartableSurface,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  INamedDocument,
  ISODateType,
  isSameRef,
  Timezone,
  toISODate,
  toMomentTz,
  toTimestamp,
  WithPrimaryKey,
} from '@principle-theorem/shared';
import { uniq } from 'lodash';
import { getPractitionerOrDefaultMapping } from '../../../mappings/staff';
import {
  type IOasisPatientNote,
  type OasisPatientNote,
} from '../entities/patient-notes';

export interface IOasisNoteSurfaceData {
  serviceCode?: string;
  surfaces: ChartableSurface[];
}

export const TOOTH_SURFACES = ['M', 'O', 'I', 'D', 'La', 'B', 'L', 'P'];

export enum OasisToothSurface {
  Occlusal = 'O',
  Buccal = 'B',
  Lingual = 'L',
  Mesial = 'M',
  Distal = 'D',
  Labial = 'La',
  Palatal = 'P',
  Incisal = 'I',
}

export const toothSurfacesMap: Record<OasisToothSurface, ChartableSurface> = {
  M: ChartableSurface.Mesial,
  O: ChartableSurface.Occlusal,
  I: ChartableSurface.Occlusal,
  D: ChartableSurface.Distal,
  B: ChartableSurface.Facial,
  La: ChartableSurface.Facial,
  L: ChartableSurface.Lingual,
  P: ChartableSurface.Lingual,
};

export class OasisNoteDataTransformer {
  static convertToToothSurfaces(item: string): ChartableSurface[] {
    if (!item) {
      return [];
    }
    const surfaces = uniq(
      item
        .replace(/-/g, '')
        .match(/([A-Z]?[^A-Z]*)/g)
        ?.slice(0, -1) ?? []
    );

    const allValidSurfaces = surfaces.every((surface) =>
      TOOTH_SURFACES.includes(surface)
    );
    if (!allValidSurfaces) {
      throw new Error(`Invalid tooth surfaces: ${surfaces.join(', ')}`);
    }
    return surfaces.map(
      (surface) => toothSurfacesMap[surface as OasisToothSurface]
    );
  }

  static getSurfaceDataFromOasisNote(
    note: string
  ): IOasisNoteSurfaceData | undefined {
    const regex = /^([A-Z]+(?:-[A-Z]+)*)(?:-(\d+))?(?=\/|\s*-)/i;
    const match = note.match(regex);
    if (!match) {
      return;
    }
    const surfaces = match[1].toUpperCase();
    const serviceCode = match[2] ?? undefined;

    return {
      serviceCode,
      surfaces: OasisNoteDataTransformer.convertToToothSurfaces(surfaces),
    };
  }

  static getOasisNoteTimestamp(
    sourceNote: IGetRecordResponse<OasisPatientNote>,
    timezone: Timezone
  ): Timestamp {
    const noteDate = OasisNoteDataTransformer.getOasisNoteDate(
      sourceNote,
      timezone
    );
    return noteDate
      ? toTimestamp(toMomentTz(noteDate, timezone))
      : toTimestamp();
  }

  static getOasisNoteDate(
    sourceNote: IGetRecordResponse<OasisPatientNote>,
    timezone: Timezone
  ): ISODateType | undefined {
    return toISODate(sourceNote.record.translations.createdAt, timezone);
  }

  static getClinicalNotePractitioner(
    practitionerId: string,
    staffMappings: WithPrimaryKey<ITranslationMap<IStaffer>>[],
    practitioners: INamedDocument<IStaffer>[]
  ): INamedDocument<IStaffer> {
    const practitionerMap = getPractitionerOrDefaultMapping(
      practitionerId,
      staffMappings
    )?.destinationIdentifier;

    if (!practitionerMap) {
      throw new Error(`Couldn't find practitioner with id ${practitionerId}`);
    }

    const practitioner = practitioners.find((searchPractitioner) =>
      isSameRef(searchPractitioner, practitionerMap)
    );

    if (!practitioner) {
      throw new Error(
        `Couldn't find practitioner with id ${practitionerId} in the provided practitioners`
      );
    }

    return practitioner;
  }

  /**
   * Oasis adds an item's description to the notes each time the item is
   * completed, so identical lines are listed once with how many times they were
   * recorded. A line includes its tooth number, so the same text on different
   * teeth is never counted together.
   */
  static getClinicalNoteLines(
    notes: Pick<IOasisPatientNote, 'note' | 'toothNumber'>[]
  ): string[] {
    const lineCounts = notes
      .map((note) => OasisNoteDataTransformer._toClinicalNoteLine(note))
      .reduce(
        (counts, line) => counts.set(line, (counts.get(line) ?? 0) + 1),
        new Map<string, number>()
      );

    return Array.from(lineCounts, ([line, count]) =>
      count > 1 ? `${line} (recorded ${count} times)` : line
    );
  }

  private static _toClinicalNoteLine(
    note: Pick<IOasisPatientNote, 'note' | 'toothNumber'>
  ): string {
    if (!note.toothNumber) {
      return note.note.trim();
    }
    return `(Tooth Number: ${note.toothNumber}) ${note.note}`.trim();
  }
}
